import type {
    AxiosError,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios'
import axios from 'axios'

import { getLocalStorageValue } from '../hooks/use-local-storage'
import {
    addHttpErrorBreadcrumb,
    addHttpRequestBreadcrumb,
    captureSentryException,
} from '../lib/capture-sentry-error'
import { getJwtState } from '../lib/getJwtState'

import { clearAuthStorage, getAccessToken, refreshAccessToken } from './auth'
import { resolveApiBaseUrl } from './resolve-api-base-url'

/**
 * Subset of AxiosRequestConfig
 */
export type RequestConfig<TData = unknown> = {
    baseURL?: string
    data?: FormData | TData
    headers?: AxiosRequestConfig['headers']
    method: 'DELETE' | 'GET' | 'OPTIONS' | 'PATCH' | 'POST' | 'PUT'
    params?: unknown
    responseType?: 'arraybuffer' | 'blob' | 'document' | 'json' | 'stream' | 'text'
    signal?: AbortSignal
    url?: string
}
/**
 * Subset of AxiosResponse
 */
export type TResponseConfig<TData = unknown> = {
    data: TData
    headers: AxiosResponse['headers']
    status: number
    statusText: string
}

export type TErrorStruct = {
    /**
     * @type integer | undefined
     */
    error_code?: number
    /**
     * @type string | undefined
     */
    error_message?: string
}

// Расширяем тип конфига, чтобы TS не ругался на кастомное поле _retry
type RetryableConfig = InternalAxiosRequestConfig & {
    _retry?: boolean
}

export interface IErrorStructWithValidationErrors extends TErrorStruct {
    validation_errors?: {
        error_message: string
        field_key: string
    }[]
}

export type ResponseErrorConfig<TError = IErrorStructWithValidationErrors> = TError

export type Client = <TData = unknown, _TError = unknown, TVariables = unknown>(
    config: RequestConfig<TVariables>,
) => Promise<TResponseConfig<TData>>

export const AXIOS_INSTANCE = axios.create({
    // ВРЕМЕННО: resolveApiBaseUrl() поддерживает ?apiurl=indelsb для тестов drivee-id на проде
    baseURL: resolveApiBaseUrl(),
    withCredentials: true,
})

const redirectToErrorPage = (title: string, description: string): void => {
    const searchParams = new URLSearchParams({
        title,
        description,
    })

    globalThis.location.href = `/error?${searchParams.toString()}`
}

AXIOS_INSTANCE.interceptors.request.use((config) => {
    config.headers = config.headers ?? {}
    const accessToken = getLocalStorageValue('access-token', '')

    if (!accessToken) {
        // Сессия не готова: пусть ошибка уйдет в общий flow
        return Promise.reject(new Error('Missing access token'))
    }
    const jwtState = getJwtState(accessToken)

    config.headers['Authorization'] = `Bearer ${accessToken}`
    if (jwtState.payload?.CityID) {
        config.headers['city-id'] = jwtState.payload.CityID
    }
    if (jwtState.payload?.UserID) {
        config.headers['user-id'] = jwtState.payload.UserID
    }

    addHttpRequestBreadcrumb(config)

    return config
})

// Interceptor для отправки ошибок в Sentry
AXIOS_INSTANCE.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        addHttpErrorBreadcrumb(error)

        // Отправляем в Sentry только серверные ошибки (5xx) и критические ошибки
        // Не отправляем клиентские ошибки (4xx), так как они могут быть ожидаемыми
        if (error.response) {
            const status = error.response.status
            const originalRequest = error.config as RetryableConfig

            if (status === 401 && !originalRequest._retry) {
                originalRequest._retry = true

                const currentRefreshToken = getLocalStorageValue('refresh-token', '')

                if (!currentRefreshToken) {
                    clearAuthStorage()

                    throw error
                }

                const retryWithAccessToken = (access: string) => {
                    const retryConfig = {
                        ...originalRequest,
                        headers: {
                            ...originalRequest.headers,
                            Authorization: `Bearer ${access}`,
                        },
                    }

                    return AXIOS_INSTANCE(retryConfig)
                }

                try {
                    const response = await refreshAccessToken(currentRefreshToken)

                    return retryWithAccessToken(response.access)
                } catch (refreshError) {
                    if (axios.isAxiosError(refreshError) && refreshError.response?.status === 401) {
                        const phone = getLocalStorageValue('user-phone', '')
                        const token = getLocalStorageValue('user-token', '')

                        if (phone && token) {
                            try {
                                const response = await getAccessToken(phone, token)

                                return retryWithAccessToken(response.access)
                            } catch {
                                // fallback не удался — обработаем ниже
                            }
                        }
                    }

                    captureSentryException(refreshError, {
                        tags: {
                            errorType: 'auth-refresh-failed',
                            originalStatus: 401,
                        },
                        extra: {
                            originalUrl: originalRequest.url,
                            refreshError:
                                refreshError instanceof Error ? refreshError.message : refreshError,
                        },
                    })
                    clearAuthStorage()
                    redirectToErrorPage(
                        'Не удалось авторизоваться',
                        'Откройте экран заново из приложения',
                    )

                    throw refreshError
                }
            }

            // Отправляем только серверные ошибки (500-599)
            if (status >= 500) {
                captureSentryException(error, {
                    tags: {
                        errorType: 'axios',
                        statusCode: status,
                        url: error.config?.url || 'unknown',
                        method: error.config?.method || 'unknown',
                    },
                    extra: {
                        response: error.response.data,
                        request: {
                            url: error.config?.url,
                            method: error.config?.method,
                            data: error.config?.data,
                        },
                    },
                })
                const searchParams = new URLSearchParams({
                    title: 'Server error',
                    description: status.toString(),
                    error: `Ошибка сервера: ${status}`,
                })

                globalThis.location.href = `/error?${searchParams.toString()}`
            }
        } else if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
            // Отмена запроса (AbortSignal, размонтирование) — не сбой сети
        } else if (error.request) {
            // Ошибка сети (нет ответа от сервера): таймаут, обрыв, офлайн
            captureSentryException(error, {
                tags: {
                    errorType: 'axios-network',
                    url: error.config?.url || 'unknown',
                    method: error.config?.method || 'unknown',
                },
                extra: {
                    request: {
                        url: error.config?.url,
                        method: error.config?.method,
                    },
                },
            })
        } else {
            // Ошибка при настройке запроса
            captureSentryException(error, {
                tags: {
                    errorType: 'axios-config',
                },
            })
        }

        // Пробрасываем ошибку дальше для обработки в catch
        throw error
    },
)

export const axiosClient: Client = async <TData, _TError = unknown, TVariables = unknown>(
    config: RequestConfig<TVariables>,
): Promise<TResponseConfig<TData>> => {
    try {
        return await AXIOS_INSTANCE.request<TData, TResponseConfig<TData>>(config)
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            // Создаём кастомную ошибку с сохранением стека
            const apiError = new Error(
                error.response.data?.error_message || 'API Error',
            ) as Error & {
                errorCode?: number
                originalError: AxiosError
                statusCode: number
            }

            apiError.statusCode = error.response.status
            apiError.errorCode = error.response.data?.error_code
            apiError.originalError = error

            throw apiError
        }
        throw error
    }
}

export default axiosClient
